import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Portal theme: 'light' | 'dark' | 'system'.
 *
 * DELIBERATELY CHEAP. The last attempt at dark mode was reverted for hurting the UI and the data-load time,
 * so this one does the least work that can possibly produce a theme:
 *
 *   * the ONLY runtime effect is setting one attribute, `data-theme`, on <html>. Everything visual comes from
 *     static CSS in the bundle (src/theme-dark.css, generated). No colours are computed in JS, no styles are
 *     injected, and nothing is recomputed while data loads.
 *   * the initial value is applied by an inline script in public/index.html BEFORE React mounts, so there is
 *     no white flash and no re-render caused by the theme.
 *   * the context value only changes when someone actually toggles, so no consumer re-renders during a fetch.
 *
 * Data fetching is untouched by any of this.
 */
const ThemeContext = createContext(null);

const STORE_KEY = 'portalTheme';
const VALID = new Set(['light', 'dark', 'system']);

export const readStoredTheme = () => {
    try {
        const saved = localStorage.getItem(STORE_KEY);
        return VALID.has(saved) ? saved : 'system';
    } catch (error) {
        return 'system';                     // private mode / storage disabled
    }
};

const prefersDark = () => {
    try {
        return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch (error) {
        return false;
    }
};

/** The theme actually painted, once 'system' is resolved. */
export const resolveTheme = (choice) => (choice === 'system' ? (prefersDark() ? 'dark' : 'light') : choice);

const applyTheme = (choice) => {
    const resolved = resolveTheme(choice);
    document.documentElement.dataset.theme = resolved;
    return resolved;
};

export function ThemeProvider({ children }) {
    const [choice, setChoice] = useState(readStoredTheme);

    useEffect(() => {
        applyTheme(choice);
        try { localStorage.setItem(STORE_KEY, choice); } catch (error) { /* storage disabled */ }
    }, [choice]);

    // Follow the OS only while the user has actually chosen 'system'.
    useEffect(() => {
        if (choice !== 'system' || !window.matchMedia) return undefined;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => applyTheme('system');
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, [choice]);

    const setTheme = useCallback((next) => setChoice(VALID.has(next) ? next : 'system'), []);
    // Cycles through the two explicit modes; 'system' stays reachable from the account menu.
    const toggleTheme = useCallback(() => setChoice((prev) => (resolveTheme(prev) === 'dark' ? 'light' : 'dark')), []);

    const value = useMemo(() => ({
        theme: choice,
        resolved: resolveTheme(choice),
        setTheme,
        toggleTheme,
    }), [choice, setTheme, toggleTheme]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) throw new Error('useTheme must be used within ThemeProvider');
    return context;
};
